import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import SearchableSelect from '../../common/SearchableSelect';
import { useModulePermissions } from '../../../hooks/useModulePermissions';
import { AccountUse } from '../../../types/finance';
import { useChart } from './useChart';

type CategoryUse = Exclude<AccountUse, 'payment'>;

const OPTIONS_FOR: Record<CategoryUse, 'feeHeads' | 'expenseHeads' | 'incomeHeads'> = {
  fee: 'feeHeads', expense: 'expenseHeads', income: 'incomeHeads',
};

/**
 * "Posts to": the ledger account a category records into. Shown to people
 * who can see the chart; choosing needs `finance_accounts.write`. Empty
 * means the suggestion (shown as the placeholder) is used.
 */
const LedgerAccountField: React.FC<{
  use: CategoryUse;
  value: string;
  onChange: (code: string) => void;
  /** The server's suggestion for this category, if it exists yet. */
  suggestedCode?: string | null;
  help: string;
}> = ({ use, value, onChange, suggestedCode, help }) => {
  const { canRead, canWrite } = useModulePermissions();
  const seesLedger = canRead('finance_accounts');
  const { accounts, options } = useChart(seesLedger);

  const placeholder = useMemo(() => {
    const a = accounts.find(x => x.code === suggestedCode);
    return a ? `Suggested — ${a.code} · ${a.name}` : 'Suggested from its name';
  }, [accounts, suggestedCode]);

  if (!seesLedger) return null;
  return (
    <Box>
      <SearchableSelect
        label="Posts to (ledger account)"
        value={value}
        onChange={v => onChange(v)}
        options={options[OPTIONS_FOR[use]]}
        placeholder={placeholder}
        fullWidth
        minWidth={0}
        disabled={!canWrite('finance_accounts')}
      />
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1, display: 'block' }}>
        {help}
      </Typography>
    </Box>
  );
};

/** The `glAccountCode` part of a save: sent only when it changed and the person may choose it. */
export const accountChange = (canChoose: boolean, next: string, previous: string) =>
  canChoose && next !== previous ? { glAccountCode: next || null } : {};

export default LedgerAccountField;
